// Real-engine Tag Management (shipping tags). The maket TagManagement is a full
// CRUD UI (add / edit / delete / reset) against the MSW /settings/tags handler.
// The REAL backend GET /api/v1/settings/tags?scope= serves a STATIC per-scope tag
// vocabulary only — there is no tags table in V1, so no create/update/delete
// endpoints exist. This lean component wires the real read and renders a
// read-only list (honest: mutations are intentionally absent until a tags table
// is ratified). Response shape: { data: TagSetting[] }.

import { useQuery } from '@tanstack/react-query'
import { Tag as TagIcon } from 'lucide-react'
import { API_BASE } from '../../lib/api'
import { Badge } from '../../components/ui/badge'
import { formatDate } from '../../lib/datetime'
import { useT } from '../../i18n'
import { tagManagementI18n } from '../../i18n/settings/tag-management'
import { realTagManagementI18n } from '../../i18n/settings/real-tag-management'
import {
  TAG_COLOR_CLASSES,
  type TagColor,
  type TagScope,
  type TagSetting,
} from '../../lib/tag-colors'
import type { SettingsSurface } from './Settings'
import { DataTableShell } from '../../components/ui/data-table-shell'

type TagListResponse = { data: TagSetting[] }

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`)
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json() as Promise<T>
}

export function RealTagManagement({ surface }: { surface: SettingsSurface }) {
  const t = useT(tagManagementI18n)
  const rt = useT(realTagManagementI18n)
  const scopeParam: TagScope = surface === 'admin' ? 'all' : surface

  const { data, isLoading, isError } = useQuery<TagListResponse>({
    queryKey: ['settings', 'tags', surface],
    queryFn: () => getJson<TagListResponse>(`/settings/tags?scope=${scopeParam}`),
  })

  const tags = data?.data ?? []

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">{t.pageTitle}</h2>
          <p className="mt-1 text-sm text-slate-600 appearance-dark:text-[var(--color-text-muted)]">{t.pageDescription}</p>
        </div>
      </div>

      <div className="mt-5 rounded-lg border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] overflow-hidden">
        {isLoading && (
          <div className="p-6 text-sm text-slate-500 appearance-dark:text-[var(--color-text-muted)]">{t.states.loading}</div>
        )}
        {isError && (
          <div className="p-6 text-sm text-rose-700 appearance-dark:text-[var(--color-danger)]">{t.states.loadFailed}</div>
        )}
        {!isLoading && !isError && tags.length === 0 && (
          <div className="p-10 text-center">
            <TagIcon className="mx-auto h-8 w-8 text-slate-300 appearance-dark:text-[var(--color-text-muted)]" />
            <p className="mt-3 text-sm text-slate-500 appearance-dark:text-[var(--color-text-muted)]">{t.states.empty}</p>
          </div>
        )}
        {!isLoading && !isError && tags.length > 0 && (
          <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th className="w-12">{t.columns.color}</th>
                <th>{t.columns.label}</th>
                <th>{t.columns.scope}</th>
                <th className="text-right">{t.columns.usage}</th>
                <th>{t.columns.created}</th>
              </tr>
            </thead>
            <tbody>
              {tags.map((tag) => {
                const c = TAG_COLOR_CLASSES[tag.color as TagColor]
                const scopeLabel = t.scopeOptions[tag.scope as TagScope].label
                return (
                  <tr key={tag.id}>
                    <td className="py-2">
                      <span
                        className={`inline-block h-4 w-4 rounded-full ring-2 ring-offset-1 appearance-dark:ring-offset-[var(--color-surface)] ${c.ring} ${c.swatch}`}
                      />
                    </td>
                    <td className="py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${c.chip} appearance-dark:bg-[var(--color-surface-muted)] appearance-dark:text-[var(--color-text)] appearance-dark:ring-[var(--color-border)]`}
                      >
                        {tag.label}
                      </span>
                    </td>
                    <td className="py-2">
                      <Badge variant="default">{scopeLabel}</Badge>
                    </td>
                    <td className="py-2 text-right tabular-nums text-[var(--color-text)]">
                      {tag.usage_count}
                    </td>
                    <td className="py-2 text-xs text-[var(--color-text-muted)]">
                      {formatDate(tag.created_at, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                      <div className="text-[var(--color-text-muted)]">{tag.created_by}</div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </DataTableShell>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-400 appearance-dark:text-[var(--color-text-muted)]">
        {rt.readOnlyNote}
      </p>
    </div>
  )
}
